import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoginCredentials } from '../types';

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setError('');
      setLoading(true);
      await login(data);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden gradient-login">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Login Card — matches mobile LoginScreen.tsx exactly */}
      <div className="max-w-md w-full mx-4 relative z-10">
        {/* Logo above card */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/icare-logo.jpg"
            alt="icare.ai"
            className="w-[110px] h-[110px] rounded-2xl shadow-blue-lg"
          />
        </div>

        {/* Title & Subtitle */}
        <h2 className="text-2xl font-bold text-center text-primary-800 mb-1"
            style={{ letterSpacing: '-0.02em' }}>
          Welcome Back
        </h2>
        <p className="text-center text-primary-700 text-base font-medium opacity-80 mb-7">
          Sign in to CarePlan Pro
        </p>

        {/* Card — mobile: borderRadius 20, padding 24, borderWidth 1, borderColor cardBorderAlpha */}
        <div className="bg-white rounded-3xl p-6 border shadow-blue-lg"
             style={{
               borderColor: 'rgba(191, 219, 254, 0.5)',
             }}>

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="rounded-md bg-danger-bg border border-danger-light p-3">
                <div className="flex items-center">
                  <svg className="h-[18px] w-[18px] text-danger-dark mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  <div className="text-sm text-danger-dark font-medium">{error}</div>
                </div>
              </div>
            )}

            {/* Email — matches mobile FieldLabel + AppTextInput */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-[18px] w-[18px] text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  className="w-full pl-10 pr-3 h-[52px] bg-white/60 rounded-md text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 transition-colors"
                  style={{ borderWidth: '1.5px', borderColor: '#BFDBFE', borderRadius: '12px' }}
                  onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#BFDBFE'; }}
                  placeholder="you@careplanpro.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-danger-dark flex items-center font-medium">
                  <svg className="h-4 w-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password — gap matches mobile labelGap: marginTop 16 → mt-4 */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-[18px] w-[18px] text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type="password"
                  className="w-full pl-10 pr-3 h-[52px] bg-white/60 rounded-md text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 transition-colors"
                  style={{ borderWidth: '1.5px', borderColor: '#BFDBFE', borderRadius: '12px' }}
                  onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#BFDBFE'; }}
                  placeholder="Enter your password"
                />
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-danger-dark flex items-center font-medium">
                  <svg className="h-4 w-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Forgot password — matches mobile forgotRow marginTop 10 marginBottom 24 */}
            <div className="flex justify-end mt-2.5 mb-6">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm text-primary-600 font-medium">
                Forgot password?
              </button>
            </div>

            {/* Sign In button — mobile: height 52, borderRadius 14, gradient, shadow */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-[52px] gradient-button text-white font-semibold text-md tracking-wide disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              style={{
                borderRadius: '14px',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.5)',
                letterSpacing: '0.5px',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Divider — matches mobile dividerRow marginVertical 20 */}
            <div className="flex items-center my-5">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="mx-3 text-xs font-medium text-gray-400 tracking-widest">OR</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Register link */}
            <div className="flex justify-center">
              <span className="text-base text-gray-600">Don't have an account? </span>
              <button type="button" onClick={() => navigate('/signup')} className="text-base text-primary-600 font-semibold ml-1">
                Register
              </button>
            </div>
          </form>
        </div>

        {/* Footer — matches mobile footer marginTop 28, opacity 0.6 */}
        <p className="text-center text-xs text-primary-700 mt-7 opacity-60">
          Secure &middot; Role-Based &middot; Offline-First
        </p>
      </div>
    </div>
  );
};

export default Login;
